import { useOutletContext, useNavigate } from 'react-router-dom';
import { Button, Paper } from '@mui/material';

import { userStore } from "~/stores/user";
import { notificationStore } from "~/stores/notification";
import Page from "~/components/Page";
import InviteUserForm from "~/components/InviteUserForm";
import ForwardOwnerShipModal from "~/modals/ForwardOwnerShipModal";
import RemoveUserModal, { TSaveState } from "~/modals/RemoveUserModal";
import RemoveInvitationModal from "~/modals/RemoveInvitationModal";
import UpdateProjectUserModal from "~/modals/UpdateProjectUserModal";
import { useTrans } from '~/hooks/useTrans';
import { getProjectBreadcrumb } from "~/utils/breadcrumbs";
import { apiGetProject, apiInviteUserProject, apiUpdateProjectUserNotification } from "~/providers/api";
import { getLink } from "~/providers/route";
import { getClientUsersData, userToItem, IUserItem, IInvitedUserItem } from "~/providers/client-users-list";
import { IOutlet, IPage } from "~/interfaces";
import { IClientProject } from "~/providers/client-interfaces";
import { IUser, IUserAdmin, TUserKeyRole } from "~/providers/interfaces";
import UsersTable from "~/tables/UsersTable";
import ErrorInfo from '~/components/ErrorInfo';
import MyTitle from "~/mui/MyTitle";
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import AdminUserDataModal from "~/modals/AdminUserDataModal";

import "./style.less";

interface IState {
	loading: boolean;
	ownerEmail: string;
	users: Array<IUserItem>;
	invitedUsers: Array<IInvitedUserItem>
	ownershipUsers: Array<string>;
	transferOwnership: boolean;
	removeUserId: number;
	removeProjectInvitationId: string;
	changeUser: IUser;
	project: IClientProject;
	isVisitor: boolean;
	projectLastUpdate: string;
	noteUserData: IUserAdmin;
	errorMsg: string;
}

const INIT_STATE: IState = {
	loading: true,
	ownerEmail: "",
	users: [],
	invitedUsers: [],
	ownershipUsers: [],
	transferOwnership: false,
	removeUserId: 0,
	removeProjectInvitationId: "",
	changeUser: null,
	project: null,
	isVisitor: true,
	projectLastUpdate: "",
	noteUserData: null,
	errorMsg: "",
};

export default function ProjectUsersPage({
	isInternal = false,
}: IPage) {
	const { trans } = useTrans();
	const { lang, isCzech } = useLang();
	const { userData } = userStore(userState => ({
		userData: userState.user,
	}));
	const { setNotification } = notificationStore(notificationState => ({
		setNotification: notificationState.setNotification,
	}));
	const { state, setState, updateState } = myUseState<IState>(INIT_STATE);
	const { params }: IOutlet = useOutletContext();
	const navigate = useNavigate();

	async function loadData() {
		updateState(INIT_STATE);

		const [projectData, usersData] = await Promise.all([
			apiGetProject(isInternal ? "admin" : "client", params.projectId, lang),
			getClientUsersData(isInternal, params.projectId, userData.email, lang, isCzech),
		]);

		updateState({
			loading: false,
			...projectData.noErrors
				? {
					project: projectData.data,
					isVisitor: projectData.data.userRole === "visitor",
					projectLastUpdate: projectData.data.lastUpdate,
				}
				: {},
			...usersData.noErrors
				? {
					ownerEmail: usersData.ownerEmail,
					users: usersData.users,
					invitedUsers: usersData.invitedUsers,
					ownershipUsers: usersData.ownershipUsers,
				}
				: {},
			errorMsg: [
				...projectData.errors,
				...usersData.errors,
			].join(", "),
		});
	}

	function onInvite(msg: string, lastUpdate: string) {
		// upravime stav
		updateState({
			projectLastUpdate: lastUpdate,
		});
		// notifikace
		setNotification(msg);
		// znovu nacteme data
		loadData();
	}

	function onInviteError(errorMsg: string) {
		setNotification(errorMsg, "error");
	}

	function showTransferOwnership() {
		return state.project && (isInternal || state.project.userRole === "owner") && state.ownershipUsers.length > 0;
	}

	async function resendInvitation(email: string, role: TUserKeyRole) {
		const resendData = await apiInviteUserProject(params.projectId, {
			emailLanguage: lang,
			invitedEmail: email,
			invitedRole: role,
			lastUpdate: state.projectLastUpdate,
		}, lang);

		if (resendData.noErrors) {
			// upravime stav
			setNotification(trans("usersPage.invitationWasResend"));
			// znovu nacteme data
			loadData();
		} else {
			setNotification(resendData.errors.join(", "), "error");
		}
	}

	function saveForwardOwnership() {
		// zrusit prevod vlastnictvi pro klienta?
		updateState({
			transferOwnership: false,
		});
		setNotification(trans("usersPage.ownershipWasTransfered"));
		// znovu nacteme data
		loadData();
	}

	function saveRemoveUserModal(saveState: TSaveState) {
		if (saveState === "remove") {
			// aktualizace dat
			updateState({
				removeUserId: 0,
			});
			setNotification(trans("usersPage.userWasRemoved"));
			// znovu nacteme data
			loadData();
		} else if (saveState === "leave") {
			updateState({
				removeUserId: 0,
			});
			// opustil jsem projekt -> presmerovani
			navigate(getLink("projects"));
		}
	}

	function saveRemoveInvitationModal(projectLastUpdate: string) {
		// aktualizace dat
		setState(prev => ({
			...prev,
			invitedUsers: prev.invitedUsers.filter(invitedUser => invitedUser.data.id !== prev.removeProjectInvitationId),
			removeProjectInvitationId: "",
			projectLastUpdate,
		}));
		setNotification(trans("usersPage.invitationWasRemoved"));
	}

	function saveUpdatedProjectUserData(newUserData: IUser, lastUpdate: string) {
		// aktualizace dat
		setState(prev => {
			const users = prev.users;
			const selUser = users.filter(user => user.data.id === prev.changeUser.id)[0];

			// todo check
			Object.assign(selUser, userToItem(newUserData));

			return {
				...prev,
				users,
				changeUser: null,
				projectLastUpdate: lastUpdate,
			};
		});
		setNotification(trans("usersPage.roleWasChanged"));
	}

	async function onNotificationUpdate(rusId: number, emailNotifications: boolean) {
		const updateData = await apiUpdateProjectUserNotification(isInternal ? "admin" : "client", params.projectId, rusId, {
			emailNotifications,
			lastUpdate: state.projectLastUpdate,
		}, lang);

		if (updateData.noErrors) {
			// upravime stav
			setNotification(emailNotifications ? trans("usersPage.notificationUpdated.on") : trans("usersPage.notificationUpdated.off"));
			// aktualizace dat
			setState(prev => {
				const users = prev.users;
				const selUser = users.filter(user => user.data.id === rusId)[0];

				selUser.notification = emailNotifications;

				return {
					...prev,
					users,
					projectLastUpdate: updateData.lastUpdate,
				};
			});
		} else {
			setNotification(updateData.errors.join(", "), "error");
		}
	}

	function saveNotUserData(newData: IUserAdmin) {
		const curUsers = state.users.slice();
		const filtered = curUsers.filter(item => item.email === newData.email);

		if (filtered.length !== 0) {
			Object.assign(filtered[0].data, newData);
		}

		updateState({
			noteUserData: null,
			users: curUsers,
		});
	}

	function onPageLoad(isAuthorized: boolean) {
		if (isAuthorized) {
			loadData();
		}
	}

	return <Page checkLogged={true} title={trans("menu.users")} breadcrumbs={getProjectBreadcrumb(trans, isInternal, state.project, trans("menu.users"))} checkInternalRole={isInternal ? "salesman" : "none"}
		onLoad={onPageLoad} seoTitleTrans="seo.title.users">
		{ (isInternal || !state.isVisitor) && <div className="usersPage__topSection">
			<div className="usersPage__addUserSection">
				<Paper elevation={3} className="usersPage__paperInvite">
					<InviteUserForm isInternal={isInternal} ownerEmail={state.ownerEmail} params={params} projectLastUpdate={state.projectLastUpdate} onError={onInviteError} onInvite={onInvite} />
				</Paper>
			</div>
			{ showTransferOwnership() && <div className="usersPage__ownershipSection">
				<Paper elevation={3} className="usersPage__paperOwnership">
					<div>
						<Button color="success" variant="contained" onClick={() => updateState({ transferOwnership: true })}>
							{ trans("usersPage.btnForwardOwnership") }
						</Button>
					</div>
				</Paper>
			</div> }
		</div> }
		<Paper elevation={3} className="usersPage__paperTable">
			<MyTitle className="margin-bottom-large">
				{ trans("usersPage.title") }
			</MyTitle>
			<UsersTable isVisitor={state.isVisitor} isInternal={isInternal} email={userData.email} ownerEmail={state.ownerEmail} loading={state.loading} users={state.users} invitedUsers={state.invitedUsers}
				onRoleUpdate={user => updateState({ changeUser: user })} onRemoveUser={user => updateState({ removeUserId: user.id })} onRemoveInvitation={invitation => updateState({ removeProjectInvitationId: invitation.id })}
				onResendInvitation={resendInvitation} onNotificationUpdate={onNotificationUpdate} onUserDataEdit={user => updateState({ noteUserData: user })} />
			{ state.errorMsg && <ErrorInfo msg={state.errorMsg} className="margin-top-large" /> }
		</Paper>
		{ state.transferOwnership && state.ownershipUsers.length > 0 && <ForwardOwnerShipModal users={state.ownershipUsers} params={params} isInternal={isInternal}
			projectLastUpdate={state.projectLastUpdate} onClose={() => updateState({ transferOwnership: false })} onSave={saveForwardOwnership} /> }
		{ state.removeUserId > 0 && <RemoveUserModal userId={state.removeUserId} params={params} isInternal={isInternal}
			projectLastUpdate={state.projectLastUpdate} onClose={() => updateState({ removeUserId: 0 })} onSave={saveRemoveUserModal} /> }
		{ state.removeProjectInvitationId && <RemoveInvitationModal projectInvitationId={state.removeProjectInvitationId} params={params} isInternal={isInternal}
			projectLastUpdate={state.projectLastUpdate} onClose={() => updateState({ removeProjectInvitationId: "" })} onSave={saveRemoveInvitationModal} /> }
		{ state.changeUser && <UpdateProjectUserModal userData={state.changeUser} params={params} isInternal={isInternal}
			projectLastUpdate={state.projectLastUpdate} onClose={() => updateState({ changeUser: null })} onSave={saveUpdatedProjectUserData} /> }
		{ state.noteUserData && <AdminUserDataModal userData={state.noteUserData} onClose={() => updateState({ noteUserData: null })} onSave={saveNotUserData} /> }
	</Page>;
}
